import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { submitServiceInquiry } from "../utils/api";

/**
 * ServiceInquiryModal
 * Prefilled "Request information" modal for the upsell pill bar. Extracted from
 * the (now-superseded) ServicePillBar.jsx quote modal and made standalone.
 *
 * Props:
 *   open          bool                              -> controls visibility
 *   onClose       () => void                        -> close handler (X / overlay / Esc)
 *   currentUser   { name, email, company }          -> DISPLAY prefill only
 *   service       string                            -> dropdown option to preselect (editable)
 *   onSubmitted   () => void (optional)             -> called after a successful submit
 *
 * SECURITY: name/email/company are prefilled for UX only. On submit we send
 * ONLY { service, message }. The backend derives identity (name/email/company/
 * tenant) from the validated token — never trust client-supplied identity here.
 */

// Exact dropdown options — must match the inquiryService values on each tile.
const SERVICE_OPTIONS = [
  "MXDR365 - Managed Detection & Response",
  "SecureShield365 Managed Services",
  "Security, Compliance & Identity Deployments",
  "Cloud Solution Provider",
  "Patriot Academy",
];

export default function ServiceInquiryModal({
  open,
  onClose,
  currentUser = {},
  service = "",
  onSubmitted,
}) {
  const [selectedService, setSelectedService] = useState(service);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // When the modal opens (or the preselected service / identity changes),
  // (re)prime the form from the current user and reset transient state.
  useEffect(() => {
    if (!open) return;
    setSelectedService(service);
    setForm({
      name: currentUser.name || "",
      email: currentUser.email || "",
      company: currentUser.company || "",
      phone: "",
      message: "",
    });
    setError("");
    setSubmitted(false);
    setSubmitting(false);
  }, [open, service, currentUser.name, currentUser.email, currentUser.company]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit() {
    if (!form.name.trim() || !form.email.trim() || !selectedService) {
      setError("Please add your name, email, and a service.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      // Identity is derived server-side from the token. Send only what the user controls.
      await submitServiceInquiry({ service: selectedService, message: form.message });
      setSubmitted(true);
      onSubmitted?.();
    } catch (e) {
      setError("Something went wrong. Please try again or call 1-844-560-4630.");
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="sim-wrap">
      <style>{CSS}</style>
      <div
        className="sim-overlay"
        onClick={(e) => { if (e.target.classList.contains("sim-overlay")) onClose?.(); }}
      >
        <div className="sim-modal" role="dialog" aria-modal="true">
          {!submitted ? (
            <>
              <div className="sim-mhead">
                <div className="sim-mtitle">Request information</div>
                <button className="sim-x" onClick={onClose} aria-label="Close">×</button>
              </div>
              <div className="sim-msub">Your details are filled in from your account. Add a note and we'll follow up.</div>

              <div className="sim-row">
                <div className="sim-field">
                  <div className="sim-label">Full Name <span className="sim-req">*</span><span className="sim-pf">from account</span></div>
                  <input className="sim-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
                </div>
                <div className="sim-field">
                  <div className="sim-label">Email Address <span className="sim-req">*</span><span className="sim-pf">from account</span></div>
                  <input className="sim-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@company.com" />
                </div>
              </div>

              <div className="sim-row">
                <div className="sim-field">
                  <div className="sim-label">Company</div>
                  <input className="sim-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Company Name" />
                </div>
                <div className="sim-field">
                  <div className="sim-label">Phone Number</div>
                  <input className="sim-input" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" />
                </div>
              </div>

              <div className="sim-field sim-full">
                <div className="sim-label">Service of Interest <span className="sim-req">*</span><span className="sim-pf">pre-selected</span></div>
                <select className="sim-select" value={selectedService} onChange={(e) => setSelectedService(e.target.value)}>
                  <option value="">Select a service...</option>
                  {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              <div className="sim-field sim-full" style={{ marginTop: 12 }}>
                <div className="sim-label">Message</div>
                <textarea className="sim-textarea" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us what you'd like to discuss..." />
              </div>

              <button className="sim-submit" disabled={submitting} onClick={handleSubmit}>
                {submitting ? "Sending..." : "Send Message  →"}
              </button>
              {error && <div className="sim-err">{error}</div>}
              <div className="sim-foot">By submitting, you agree to our Privacy Policy.</div>
            </>
          ) : (
            <div className="sim-success">
              <div className="sim-success-ic">✓</div>
              <div className="sim-mtitle" style={{ marginBottom: 6 }}>Message sent</div>
              <div className="sim-msub" style={{ marginBottom: 0 }}>
                Your account team will follow up about {selectedService} shortly.
              </div>
              <button className="sim-submit" style={{ marginTop: 18 }} onClick={onClose}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

const CSS = `
.sim-wrap{font-family:'DM Sans',system-ui,sans-serif;}
.sim-overlay{position:fixed;inset:0;background:rgba(6,16,32,.72);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:12000;padding:20px;}
.sim-modal{width:100%;max-width:460px;background:#0e2746;border:1px solid rgba(255,255,255,.12);border-radius:18px;padding:22px;box-shadow:0 24px 60px rgba(0,0,0,.55);color:#edf4ff;max-height:92vh;overflow-y:auto;}
.sim-mhead{display:flex;align-items:flex-start;justify-content:space-between;}
.sim-mtitle{font-size:18px;font-weight:800;color:#fff;}
.sim-msub{font-size:12px;color:#8aa3c4;margin-bottom:16px;}
.sim-x{background:none;border:none;color:#8aa3c4;font-size:22px;line-height:1;cursor:pointer;}
.sim-x:hover{color:#fff;}
.sim-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;}
.sim-field{display:flex;flex-direction:column;}
.sim-full{grid-column:1/-1;}
.sim-label{font-size:11px;font-weight:700;color:#c2d2e8;margin-bottom:5px;}
.sim-req{color:#e14d57;}
.sim-pf{font-size:9px;font-weight:700;color:#53c46d;background:rgba(83,196,109,.12);padding:1px 6px;border-radius:999px;margin-left:6px;}
.sim-input,.sim-select,.sim-textarea{width:100%;box-sizing:border-box;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.14);border-radius:10px;padding:9px 11px;font-size:13px;color:#edf4ff;font-family:inherit;}
.sim-input::placeholder,.sim-textarea::placeholder{color:#6f8bb0;}
.sim-input:focus,.sim-select:focus,.sim-textarea:focus{outline:none;border-color:#44a6ff;}
.sim-select option{background:#0e2746;color:#edf4ff;}
.sim-textarea{resize:vertical;min-height:84px;}
.sim-submit{width:100%;margin-top:4px;padding:12px;border-radius:11px;border:none;background:#c1121f;color:#fff;font-size:14px;font-weight:800;font-family:inherit;cursor:pointer;}
.sim-submit:hover{background:#9d0e19;}
.sim-submit:disabled{opacity:.6;cursor:default;}
.sim-foot{font-size:10px;color:#6f8bb0;text-align:center;margin-top:12px;}
.sim-err{font-size:11px;color:#e14d57;margin-top:8px;text-align:center;}
.sim-success{text-align:center;padding:14px 6px;}
.sim-success-ic{width:52px;height:52px;border-radius:50%;background:rgba(83,196,109,.15);border:1px solid rgba(83,196,109,.4);display:flex;align-items:center;justify-content:center;margin:0 auto 14px;color:#53c46d;font-size:26px;}
`;
